import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import useInfiniteScroll from '~utils/hooks/useInfiniteScroll';

import CheckboxFilterMenuItem from './CheckboxFilterMenuItem';
import SearchField from './SearchField';

export const FILTER_MENU_ITEM_HEIGHT_IN_PX = 32;
const VISIBLE_FILTER_MENU_ITEMS_COUNT = 10;

export type FilterMenuOption = {
  label: string;
  value: string;
};

export enum FilterMenuItemLoadingReason {
  SEARCH_VALUE = 'search_value',
  INFINITE_SCROLL = 'infinite_scroll',
}

interface FilterMenuItemProps {
  focusSearchFieldOnMount?: boolean;
  loadingReason?: `${FilterMenuItemLoadingReason}`;
  onFetchMore?: (() => Promise<void>) | (() => void);
  onRequestBack?: () => void;
  onRequestClose?: () => void;
  onSearchValueChange?: (value: string) => void;
  onSelectedOptionsChange: (options: string[], selectedOption: string | null) => void;
  options: FilterMenuOption[];
  searchValue?: string;
  selectAllOptionLabel?: string;
  selectNoneOptionLabel?: string;
  selectedOptions: string[];
}

export default function FilterMenuItem({
  focusSearchFieldOnMount,
  loadingReason,
  onFetchMore,
  onRequestBack,
  onRequestClose,
  onSearchValueChange,
  onSelectedOptionsChange,
  options,
  searchValue,
  selectAllOptionLabel,
  selectNoneOptionLabel,
  selectedOptions,
}: FilterMenuItemProps) {
  const infiniteScroll = useInfiniteScroll({ threshold: 0.5 });

  // Keyboard navigation over the options list: the search field owns focus, so arrow keys
  // move this highlight and Enter/Space toggles the highlighted option. -1 = nothing yet.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  // Space toggles only while navigating by keyboard; a mouse-hover highlight must not
  // hijack the space bar from typing a multi-word query into the search field.
  const highlightViaKeyboardRef = useRef(false);

  const toggleOptionById = useCallback(
    (id: string) => {
      let effectiveId: string | null = id;

      if (selectedOptions.length === 0) {
        onSelectedOptionsChange([id], id);
      } else {
        const index = selectedOptions.findIndex((value) => value === id);
        const newSelectedOptions = [...selectedOptions];

        if (index > -1) {
          newSelectedOptions.splice(index, 1);
          effectiveId = null;
        } else {
          newSelectedOptions.push(id);
        }

        onSelectedOptionsChange(newSelectedOptions, effectiveId);
      }
    },
    [selectedOptions, onSelectedOptionsChange],
  );

  const handleOptionClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      toggleOptionById(event.currentTarget.id);
    },
    [toggleOptionById],
  );

  // Typing (new results) resets the highlight back to the search field.
  useEffect(() => {
    setHighlightedIndex(-1);
    highlightViaKeyboardRef.current = false;
  }, [searchValue]);

  useEffect(() => {
    if (highlightedIndex < 0) {
      return;
    }

    const node = optionsContainerRef.current?.children[highlightedIndex];
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex]);

  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Escape steps back out of the value list (e.g. to the category list).
      if (event.key === 'Escape') {
        if (onRequestBack) {
          event.preventDefault();
          onRequestBack();
        }

        return;
      }

      // Selections apply live as they're toggled, so Enter just confirms & dismisses.
      if (event.key === 'Enter') {
        if (onRequestClose) {
          event.preventDefault();
          onRequestClose();
        }

        return;
      }

      if (options.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        highlightViaKeyboardRef.current = true;
        setHighlightedIndex((index) => Math.min(options.length - 1, index + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        highlightViaKeyboardRef.current = true;
        setHighlightedIndex((index) => Math.max(0, index - 1));
      } else if (event.key === ' ') {
        const highlightedOption = options[highlightedIndex];

        // Space toggles only when the highlight came from keyboard nav; otherwise it
        // types a space into the search field (e.g. multi-word queries).
        if (highlightedOption && highlightViaKeyboardRef.current) {
          event.preventDefault();
          toggleOptionById(highlightedOption.value);
        }
      }
    },
    [options, highlightedIndex, toggleOptionById, onRequestClose, onRequestBack],
  );

  const handleSelectAllOptionClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      onSelectedOptionsChange([], null);
    },
    [onSelectedOptionsChange],
  );

  useEffect(() => {
    if (!loadingReason && onFetchMore && infiniteScroll.endReached) {
      onFetchMore()?.then(() => {
        infiniteScroll.notify();
      });
    }
  }, [onFetchMore, infiniteScroll.endReached, loadingReason]);

  return (
    <>
      {onSearchValueChange && (
        <>
          <SearchField
            isLoading={loadingReason === FilterMenuItemLoadingReason.SEARCH_VALUE}
            onChange={onSearchValueChange}
            onKeyDown={handleSearchKeyDown}
            setFocusOnMount={focusSearchFieldOnMount}
            value={searchValue ?? ''}
          />
          <Divider sx={{ '&.MuiDivider-root': { mt: 0, mb: 1, mx: 2 } }} />
        </>
      )}
      {selectAllOptionLabel && (
        <>
          <CheckboxFilterMenuItem
            checked={selectedOptions.length === 0}
            label={selectAllOptionLabel}
            onClick={handleSelectAllOptionClick}
          />
          <Divider sx={{ '&.MuiDivider-root': { mx: 2 } }} />
        </>
      )}
      {selectNoneOptionLabel && (
        <CheckboxFilterMenuItem
          checked={selectedOptions.includes('none')}
          id="none"
          label={selectNoneOptionLabel}
          onClick={handleOptionClick}
        />
      )}
      {options.length > 0 && (
        <Box
          ref={optionsContainerRef}
          sx={{
            maxHeight: FILTER_MENU_ITEM_HEIGHT_IN_PX * VISIBLE_FILTER_MENU_ITEMS_COUNT,
            overflow: 'auto',
          }}
        >
          {options.map((option, index) => (
            <React.Fragment key={option.value}>
              <CheckboxFilterMenuItem
                checked={selectedOptions.includes(option.value)}
                highlighted={index === highlightedIndex}
                id={option.value}
                label={option.label}
                onClick={handleOptionClick}
                onMouseEnter={() => {
                  highlightViaKeyboardRef.current = false;
                  setHighlightedIndex(index);
                }}
              />

              {index === options.length - 1 && onFetchMore && (
                <Box
                  ref={infiniteScroll.nodeRef}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    height: FILTER_MENU_ITEM_HEIGHT_IN_PX,
                    justifyContent: 'center',
                  }}
                >
                  <LoadingSpinner
                    loadingIndicatorSize={16}
                    isVisible={
                      loadingReason === FilterMenuItemLoadingReason.INFINITE_SCROLL
                    }
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </>
  );
}
